import {
  boardRectToScreen,
  boardToScreen,
  screenToBoard,
  type ImageNode,
  type Rect,
  type ResizeHandle,
  type Vec,
  type Viewport,
} from '@kanvas/board-core';
import { activeCrop, cropSourceBox, savedCropSource, toImageLocal } from '../interaction/gestures/crop';
import { HANDLE_HIT_SLOP, HANDLE_SIZE } from './handles';

const CROP_HANDLES: ResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

export { cropSourceBox };

export function hitCropHandle(
  node: ImageNode,
  vp: Viewport,
  screen: Vec,
): ResizeHandle | 'move' | 'draw' | null {
  const crop = activeCrop(node);
  const source = savedCropSource(node);
  // The handles are laid out in the image's un-rotated frame; test the pointer
  // there too.
  if (node.rotation !== 0) {
    screen = boardToScreen(vp, toImageLocal(node, screenToBoard(vp, screen)));
  }
  const win: Rect = {
    x: source.x + crop.x * source.w,
    y: source.y + crop.y * source.h,
    w: crop.w * source.w,
    h: crop.h * source.h,
  };
  const screenWin = boardRectToScreen(vp, win);
  const slop = HANDLE_SIZE / 2 + HANDLE_HIT_SLOP;

  const points: Record<ResizeHandle, Vec> = {
    nw: { x: screenWin.x, y: screenWin.y },
    n: { x: screenWin.x + screenWin.w / 2, y: screenWin.y },
    ne: { x: screenWin.x + screenWin.w, y: screenWin.y },
    e: { x: screenWin.x + screenWin.w, y: screenWin.y + screenWin.h / 2 },
    se: { x: screenWin.x + screenWin.w, y: screenWin.y + screenWin.h },
    s: { x: screenWin.x + screenWin.w / 2, y: screenWin.y + screenWin.h },
    sw: { x: screenWin.x, y: screenWin.y + screenWin.h },
    w: { x: screenWin.x, y: screenWin.y + screenWin.h / 2 },
  };

  for (const id of CROP_HANDLES) {
    const p = points[id]!;
    if (Math.abs(p.x - screen.x) <= slop && Math.abs(p.y - screen.y) <= slop) return id;
  }

  const srcScreen = boardRectToScreen(vp, source);
  const inSource =
    screen.x >= srcScreen.x &&
    screen.x <= srcScreen.x + srcScreen.w &&
    screen.y >= srcScreen.y &&
    screen.y <= srcScreen.y + srcScreen.h;

  if (!inSource) return null;

  // Inside the image: freehand draw by default; 'move' is reserved for Alt.
  return 'draw';
}

/** Content area of an open notebook page, in board coordinates. */
export function notebookContentBox(node: {
  x: number;
  y: number;
  w: number;
  h: number;
}): Rect {
  const spine = Math.min(14, node.w * 0.06);
  return {
    x: node.x + spine + 10,
    y: node.y + 12,
    w: node.w - spine - 20,
    h: node.h - 24,
  };
}

export function boardPointInRect(p: Vec, r: Rect): boolean {
  return p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
}

export function boardToScreenPoint(vp: Viewport, p: Vec): Vec {
  return boardToScreen(vp, p);
}
